import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  FormControlLabel,
  Switch,
  InputAdornment,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import {
  useCreateConceptoAporte,
  useUpdateConceptoAporte,
} from '../../hooks/useConceptosAporteAdmin';
import type { ConceptoAporte } from '../../types/afiliado';

const schema = z
  .object({
    // Código: 2-30 chars, solo MAYÚSCULAS/dígitos/guión bajo. Trim previo.
    codigo: z
      .string({ required_error: 'Código es requerido' })
      .trim()
      .min(2, 'Código debe tener al menos 2 caracteres')
      .max(30, 'Código no puede superar 30 caracteres')
      .regex(/^[A-Z0-9_]+$/i, 'Solo letras, números y guión bajo'),
    descripcion: z
      .string({ required_error: 'Descripción es requerida' })
      .trim()
      .min(3, 'Descripción debe tener al menos 3 caracteres')
      .max(200, 'Descripción no puede superar 200 caracteres'),
    porcentaje: z.coerce
      .number({ invalid_type_error: 'Porcentaje debe ser numérico' })
      .min(0, 'Porcentaje no puede ser negativo')
      .max(100, 'Porcentaje no puede superar 100'),
    aplica_afiliados: z.boolean(),
    aplica_no_afiliados: z.boolean(),
    opcional: z.boolean(),
    activo: z.boolean(),
  })
  .refine(
    (v) => v.opcional || v.aplica_afiliados || v.aplica_no_afiliados,
    {
      message:
        'Si no es opcional, debe aplicar al menos a afiliados o a no afiliados (automático)',
      path: ['opcional'],
    }
  );

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  codigo: '',
  descripcion: '',
  porcentaje: 0,
  aplica_afiliados: false,
  aplica_no_afiliados: false,
  opcional: true, // por defecto, los nuevos son opcionales (seguros)
  activo: true,
};

interface Props {
  open: boolean;
  concepto: ConceptoAporte | null;
  onClose: () => void;
}

export default function ConceptoAporteDialog({ open, concepto, onClose }: Props) {
  const isEdit = !!concepto;
  const create = useCreateConceptoAporte();
  const update = useUpdateConceptoAporte();

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      reset(
        concepto
          ? {
              codigo: concepto.codigo,
              descripcion: concepto.descripcion,
              porcentaje: concepto.porcentaje,
              aplica_afiliados: concepto.aplica_afiliados,
              aplica_no_afiliados: concepto.aplica_no_afiliados,
              opcional: concepto.opcional,
              activo: concepto.activo,
            }
          : defaultValues
      );
    }
  }, [open, concepto, reset]);

  const opcional = watch('opcional');

  const onSubmit = async (values: FormValues) => {
    try {
      if (isEdit && concepto) {
        await update.mutateAsync({ id: concepto.id, payload: values });
      } else {
        await create.mutateAsync(values);
      }
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const apiError = (isEdit ? update.error : create.error) as Error | null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle>{isEdit ? 'Editar concepto' : 'Nuevo concepto de aporte'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {apiError && (
              <Alert severity="error">
                {apiError.message.includes('duplicate')
                  ? 'Ya existe un concepto con ese codigo'
                  : apiError.message}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                {...register('codigo')}
                required
                fullWidth
                label="Codigo"
                placeholder="SINDICAL, VOLUNTARIO, etc."
                disabled={isEdit}
                error={!!errors.codigo}
                helperText={
                  errors.codigo?.message ??
                  (isEdit ? 'No se puede modificar el codigo' : 'Solo MAYUSCULAS, sin espacios')
                }
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
              <Controller
                name="porcentaje"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? 0 : Number(e.target.value))
                    }
                    type="number"
                    required
                    fullWidth
                    label="Porcentaje"
                    error={!!errors.porcentaje}
                    helperText={errors.porcentaje?.message ?? 'Aplica sobre el sueldo del empleado'}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      inputProps: { min: 0, max: 100, step: 0.01 },
                    }}
                  />
                )}
              />
            </Stack>

            <TextField
              {...register('descripcion')}
              required
              fullWidth
              label="Descripcion"
              placeholder="Ej: Aporte Sindical, Fondo Cultural..."
              error={!!errors.descripcion}
              helperText={errors.descripcion?.message}
            />

            <Box
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Tipo de aplicacion
              </Typography>
              <Controller
                name="opcional"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={
                      field.value
                        ? 'Opcional — solo se aplica si se asigna explicitamente al afiliado'
                        : 'Automatico — se aplica segun afiliacion del empleado'
                    }
                  />
                )}
              />

              {!opcional && (
                <Stack direction="column" spacing={1} sx={{ mt: 1, pl: 2 }}>
                  <Controller
                    name="aplica_afiliados"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Aplicar a empleados AFILIADOS al sindicato"
                      />
                    )}
                  />
                  <Controller
                    name="aplica_no_afiliados"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Aplicar a empleados NO AFILIADOS"
                      />
                    )}
                  />
                </Stack>
              )}

              {errors.opcional && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {errors.opcional.message}
                </Alert>
              )}
            </Box>

            <Controller
              name="activo"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={field.value ? 'Activo' : 'Inactivo'}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isSubmitting ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear concepto'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
